import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Experience from './pages/Experience';
import Projects from './pages/Projects';
import Publications from './pages/Publications';
import Awards from './pages/Awards';
import { links } from './data/links';
import { content } from './data/content';
import {
  getInitialTheme,
  getInitialLanguage,
  saveTheme,
  saveLanguage,
  htmlLang,
} from './lib/preferences';
import '@fortawesome/fontawesome-free/css/all.min.css';

const App = () => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [language, setLanguage] = useState(getInitialLanguage);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    saveTheme(next);
  };

  const toggleLanguage = (lang) => {
    setLanguage(lang);
    saveLanguage(lang);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = htmlLang[language];
  }, [language]);

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300 font-sans">
        {/* Decorative background: soft gradient + blurred blobs (no external assets) */}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" />
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-400/20 dark:bg-blue-600/10 blur-3xl" />
          <div className="absolute top-1/3 -right-24 w-96 h-96 rounded-full bg-cyan-400/20 dark:bg-cyan-600/10 blur-3xl" />
        </div>

        <Navbar
          theme={theme}
          toggleTheme={toggleTheme}
          language={language}
          toggleLanguage={toggleLanguage}
        />

        <div className="relative z-10">
          <Routes>
            <Route path="/" element={<Home language={language} />} />
            <Route path="/experience" element={<Experience language={language} />} />
            <Route path="/projects" element={<Projects language={language} />} />
            <Route path="/publications" element={<Publications language={language} />} />
            <Route path="/awards" element={<Awards language={language} />} />
          </Routes>
        </div>

        {/* Footer */}
        <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-8 relative z-10 transition-colors duration-300">
          <div className="container mx-auto text-center">
            <div className="flex justify-center items-center space-x-6 mb-4">
              <a href={links.github} target="_blank" rel="noreferrer" aria-label="GitHub" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <i className="fab fa-github text-2xl"></i>
              </a>
              <a href={links.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <i className="fab fa-linkedin text-2xl"></i>
              </a>
              <a href={`mailto:${links.email}`} aria-label="Email" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <i className="fas fa-envelope text-2xl"></i>
              </a>
            </div>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              © {new Date().getFullYear()} {content[language].footer.name}{content[language].footer.separator}{content[language].footer.rights}
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;
